// src/pages/UserList.js - VERSION CORRIGÉE AVEC FILTRES PAR RÔLE
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import * as XLSX from "xlsx";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#4FC3F7',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  successLight: '#e8f5e9',
  dangerLight: '#ffebee',
  warningLight: '#fff3e0',
  infoLight: '#e3f2fd'
};

const PROFILE_OPTIONS = [
  { value: "1", label: "Administrateur" },
  { value: "2", label: "Contrôleur" },
  { value: "3", label: "Gestionnaire" },
  { value: "4", label: "Agent" },
  { value: "5", label: "Poste de contrôle" }
];

const profileLabel = (profileValue) => {
  if (profileValue === null || profileValue === undefined) return "";
  const str = String(profileValue).trim();
  if (!str) return "";
  const found = PROFILE_OPTIONS.find((opt) => opt.value === str);
  return found ? found.label : profileValue;
};

const profileIcon = (profileValue) => {
  switch (profileValue) {
    case "1": return "bi-shield-check";
    case "2": return "bi-binoculars";
    case "3": return "bi-person-badge";
    case "4": return "bi-person-workspace";
    case "5": return "bi-display";
    default: return "bi-person";
  }
};

const statusLabel = (statut) => {
  switch (statut) {
    case 1: return { text: "Actif", className: "badge bg-success", icon: "bi-check-circle" };
    case 0: return { text: "Inactif", className: "badge bg-secondary", icon: "bi-x-circle" };
    default: return { text: "Inconnu", className: "badge bg-light text-dark", icon: "bi-question-circle" };
  }
};

const UserList = () => {
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [currentSocieteId, setCurrentSocieteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [resetPasswordResult, setResetPasswordResult] = useState(null); // { password, warning }
  const [passwordCopie, setPasswordCopie] = useState(false);
  const [passwordMasque, setPasswordMasque] = useState(true);
  
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalAction, setModalAction] = useState("");
  const [modalMessage, setModalMessage] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [filterProfile, setFilterProfile] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // États pour l'import Excel
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [importResults, setImportResults] = useState([]);

  // ============================================
  // 🔥 CHARGEMENT INITIAL - RÉCUPÉRER L'UTILISATEUR CONNECTÉ
  // ============================================
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setCurrentProfile(String(userData.profile || "").trim());
        setCurrentSocieteId(Number(userData.societe_id) || 0);
        setCurrentUserId(Number(userData.id) || 0);
        console.log("🔍 Utilisateur connecté - ID:", userData.id, "Profil:", userData.profile);
      } catch (error) {
        console.error("Error parsing user:", error);
      }
    }
  }, []);

  // ============================================
  // 🔥 FONCTIONS DE PERMISSION POUR LES FILTRES
  // ============================================
  const getFilteredProfileOptions = () => {
    if (!currentProfile) return [];
    
    switch (currentProfile) {
      case "1": // Admin : voit Admin + Gestionnaire
        return PROFILE_OPTIONS.filter(opt => 
          opt.value === "1" || opt.value === "3"
        );
      case "2": // Contrôleur : voit Agent UNIQUEMENT
        return PROFILE_OPTIONS.filter(opt => 
          opt.value === "4"
        );
      case "3": // Gestionnaire : voit Contrôleur + Agent + Poste de contrôle
        return PROFILE_OPTIONS.filter(opt =>
          opt.value === "2" || opt.value === "4" || opt.value === "5"
        );
      default: 
        return [];
    }
  };

  const canUseFilters = () => {
    if (!currentProfile) return false;
    return currentProfile === "1" || currentProfile === "3";
  };

  const canUseProfileFilter = () => {
    if (!currentProfile) return false;
    return currentProfile === "1" || currentProfile === "3";
  };

  const canUseStatutFilter = () => {
    if (!currentProfile) return false;
    return currentProfile === "1" || currentProfile === "3";
  };

  // ============================================
  // 🔥 CHARGEMENT DES DONNÉES
  // ============================================
  const fetchData = async () => {
    setLoading(true);
    setServerError("");
    setInfoMsg("");
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      // Construire les paramètres de recherche
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (filterStatut && canUseStatutFilter()) params.append("statut", filterStatut);
      if (filterProfile && canUseProfileFilter()) params.append("profile", filterProfile);
      params.append("page", 1);
      params.append("limit", 1000);
      
      const url = `${API_BASE_URL}/users/list.php?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      if (response.data?.success) {
        let usersData = response.data.users || [];

        // ============================================
        // 🔥 FILTRE CRITIQUE : MASQUER L'UTILISATEUR CONNECTÉ
        // ============================================
        if (usersData.length > 0 && currentUserId) {
          usersData = usersData.filter(user => 
            Number(user.id) !== Number(currentUserId)
          );
        }

        setUsers(usersData);
        setFilteredUsers(usersData);
        
        // Messages personnalisés selon le profil
        if (usersData.length === 0) {
          if (currentProfile === "1") {
            setInfoMsg("Aucun autre administrateur ou gestionnaire trouvé.");
          } else if (currentProfile === "3") {
            setInfoMsg("Aucun contrôleur ou agent trouvé dans votre société.");
          } else if (currentProfile === "2") {
            setInfoMsg("Aucun agent trouvé dans votre société.");
          } else {
            setInfoMsg("Aucun utilisateur trouvé dans la base de données.");
          }
        }
      } else {
        setServerError(response.data?.message || "Impossible de charger la liste des utilisateurs.");
      }
    } catch (err) {
      handleFetchError(err);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // 🔥 GESTION DES ERREURS
  // ============================================
  const handleFetchError = (err) => {
    console.error("Fetch error:", err);
    if (err.response?.status === 401) {
      setServerError("Session expirée. Veuillez vous reconnecter.");
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      setTimeout(() => navigate("/login"), 2000);
    } else if (err.response?.status === 403) {
      setServerError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
    } else if (err.response?.status === 500) {
      setServerError("Erreur serveur. Veuillez contacter l'administrateur.");
    } else if (err.request) {
      setServerError("Serveur inaccessible. Vérifiez votre connexion.");
    } else {
      setServerError("Erreur technique lors du chargement des données.");
    }
  };

  // ============================================
  // 🔥 EFFET POUR CHARGER LES DONNÉES AU DÉMARRAGE
  // ============================================
  useEffect(() => {
    if (currentUserId) {
      fetchData();
    }
  }, [currentUserId]);

  // ============================================
  // 🔥 EFFET POUR APPLIQUER LES FILTRES
  // ============================================
  // ============================================
// 🔥 EFFET POUR APPLIQUER LES FILTRES - CORRIGÉ
// ============================================
useEffect(() => {
  if (users.length > 0) {
    let filtered = [...users];

    // Filtre par recherche
    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        (user.nom && user.nom.toLowerCase().includes(term)) ||
        (user.prenom && user.prenom.toLowerCase().includes(term)) ||
        (user.email && user.email.toLowerCase().includes(term)) ||
        (user.matricule && user.matricule.toLowerCase().includes(term))
      );
    }

    // 🔥 CORRECTION FILTRE STATUT
    if (filterStatut && filterStatut !== "" && canUseStatutFilter()) {
      filtered = filtered.filter(user => {
        if (filterStatut === "actif") return Number(user.statut_user) === 1;
        if (filterStatut === "inactif") return Number(user.statut_user) === 0;
        return true;
      });
    }

    // 🔥 CORRECTION FILTRE PROFIL
    if (filterProfile && filterProfile !== "" && canUseProfileFilter()) {
      filtered = filtered.filter(user => 
        String(user.profile) === String(filterProfile)
      );
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
    
    if (filtered.length === 0 && (searchTerm || filterStatut || filterProfile)) {
      setInfoMsg("Aucun utilisateur ne correspond aux critères de recherche.");
    } else {
      setInfoMsg("");
    }
  } else {
    setFilteredUsers([]);
  }
}, [users, searchTerm, filterStatut, filterProfile, currentProfile]);
  // ============================================
  // 🔥 FONCTIONS DE NAVIGATION
  // ============================================
  const handleViewDetails = (user) => {
    navigate(`/users/details/${user.id}`);
  };

  const handleCreateUser = () => {
    navigate("/users/create");
  };

  // ============================================
  // 🔥 FONCTIONS POUR LES MODALS
  // ============================================
  const openStatusModal = (user) => {
    setSelectedUser(user);
    const action = user.statut_user === 1 ? "desactiver" : "activer";
    setModalAction(action);
    setModalMessage(`${action === "activer" ? "Activer" : "Désactiver"} l'utilisateur ${user.prenom} ${user.nom} ?`);
  };

  const openResetPasswordModal = (user) => {
    setSelectedUser(user);
    setModalAction("resetPassword");
    setModalMessage(`Réinitialiser le mot de passe de ${user.prenom} ${user.nom} ?`);
    setResetPasswordResult(null);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setModalAction("delete");
    setModalMessage(`Supprimer l'utilisateur ${user.prenom} ${user.nom} ?`);
  };

  const closeAllModals = () => {
    setShowImportModal(false);
    setSelectedUser(null);
    setModalAction("");
    setModalMessage("");
    setImportErrors([]);
    setImportResults([]);
  };

  // ============================================
  // 🔥 ACTIONS SUR LES UTILISATEURS
  // ============================================
  const handleChangeStatus = async () => {
    if (!selectedUser || !selectedUser.id) return;
    
    if (selectedUser.id === currentUserId) {
      setInfoMsg("Vous ne pouvez pas désactiver votre propre compte.");
      closeAllModals();
      return;
    }

    setUpdatingId(selectedUser.id);

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        closeAllModals();
        return;
      }

      const newStatus = selectedUser.statut_user === 1 ? 0 : 1;
      const url = `${API_BASE_URL}/users/change_status.php`;
      
      const response = await axios.post(
        url,
        { id: selectedUser.id, statut_user: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success) {
        const result = newStatus === 1 ? "activé" : "désactivé";
        setInfoMsg(`Utilisateur ${result} avec succès.`);
        fetchData();
      } else {
        setInfoMsg(response.data?.message || "Échec de l'opération.");
      }
    } catch (err) {
      console.error("Erreur:", err);
      setInfoMsg(err.response?.data?.message || "Erreur technique.");
    } finally {
      setUpdatingId(null);
      closeAllModals();
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !selectedUser.id) return;
    
    setUpdatingId(selectedUser.id);

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        closeAllModals();
        return;
      }

      const url = `${API_BASE_URL}/users/reset_password.php`;
      
      const response = await axios.post(
        url,
        { id: selectedUser.id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success) {
        setInfoMsg(response.data.message || "Mot de passe réinitialisé avec succès.");
        if (response.data.generated_password) {
          setResetPasswordResult({
            password: response.data.generated_password,
            warning: response.data.warning || null,
          });
        }
      } else {
        setInfoMsg(response.data?.message || "Échec réinitialisation.");
      }
    } catch (err) {
      console.error("Erreur:", err);
      setInfoMsg("Erreur technique.");
    } finally {
      setUpdatingId(null);
      closeAllModals();
    }
  };

  const handleDelete = async () => {
    if (!selectedUser || !selectedUser.id) return;
    
    if (selectedUser.id === currentUserId) {
      setInfoMsg("Vous ne pouvez pas vous supprimer.");
      closeAllModals();
      return;
    }

    setUpdatingId(selectedUser.id);

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        closeAllModals();
        return;
      }

      const url = `${API_BASE_URL}/users/delete.php`;
      
      const response = await axios.post(
        url,
        { id: selectedUser.id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success) {
        setInfoMsg("Utilisateur supprimé avec succès.");
        fetchData();
      } else {
        setInfoMsg(response.data?.message || "Échec suppression.");
      }
    } catch (err) {
      console.error("Erreur:", err);
      setInfoMsg("Erreur technique.");
    } finally {
      setUpdatingId(null);
      closeAllModals();
    }
  };

  // ============================================
  // 🔥 IMPORT EXCEL
  // ============================================
  const handleImportExcel = () => {
    setShowImportModal(true);
    setImportFile(null);
    setImportPreview([]);
    setImportErrors([]);
    setImportResults([]);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImportFile(file);
    setImportErrors([]);
    setImportResults([]);

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length > 0) {
        const headers = jsonData[0];
        const rows = jsonData.slice(1).map((row, index) => {
          const obj = {};
          headers.forEach((header, colIndex) => {
            obj[header] = row[colIndex] || '';
          });
          return obj;
        }).filter(row => Object.values(row).some(val => val !== ''));

        setImportPreview(rows.slice(0, 10));
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const validateImportData = (data) => {
    const errors = [];
    
    data.forEach((row, index) => {
      const lineNumber = index + 2;
      
      if (!row.nom || row.nom.trim() === '') {
        errors.push(`Ligne ${lineNumber}: Le champ "nom" est requis`);
      }
      
      if (!row.prenom || row.prenom.trim() === '') {
        errors.push(`Ligne ${lineNumber}: Le champ "prenom" est requis`);
      }
      
      if (!row.email || row.email.trim() === '') {
        errors.push(`Ligne ${lineNumber}: Le champ "email" est requis`);
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(row.email.trim())) {
          errors.push(`Ligne ${lineNumber}: Email invalide`);
        }
      }
      
      if (!row.profile || row.profile.trim() === '') {
        errors.push(`Ligne ${lineNumber}: Le champ "profile" est requis (1: Admin, 2: Contrôleur, 3: Gestionnaire, 4: Agent, 5: Poste de contrôle)`);
      } else {
        const profile = row.profile.toString().trim();
        if (!['1', '2', '3', '4', '5'].includes(profile)) {
          errors.push(`Ligne ${lineNumber}: "profile" doit être 1, 2, 3, 4 ou 5`);
        }
      }
    });
    
    return errors;
  };

  const downloadExcelTemplate = () => {
    const templateData = [
      ['nom', 'prenom', 'email', 'phone', 'matricule', 'profile', 'statut_user'],
      ['Dupont', 'Jean', 'jean.dupont@example.com', '0123456789', 'EMP001', '4', 'actif'],
      ['Martin', 'Marie', 'marie.martin@example.com', '', 'EMP002', '4', 'actif'],
      ['Durand', 'Pierre', 'pierre.durand@example.com', '0987654321', 'EMP003', '2', 'inactif'],
      ['Leroy', 'Sophie', 'sophie.leroy@example.com', '', 'EMP004', '4', 'actif'],
      ['Petit', 'Luc', 'luc.petit@example.com', '0654321987', 'EMP005', '3', 'actif']
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Utilisateurs");
    
    XLSX.writeFile(wb, "modele_import_utilisateurs.xlsx");
  };

  const processImport = async () => {
    if (!importFile) {
      setImportErrors(["Veuillez sélectionner un fichier Excel"]);
      return;
    }

    setImporting(true);
    setImportErrors([]);
    setImportResults([]);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length <= 1) {
          setImportErrors(["Le fichier Excel est vide ou ne contient que des entêtes"]);
          setImporting(false);
          return;
        }

        const headers = jsonData[0];
        const rows = jsonData.slice(1).map((row, index) => {
          const obj = {};
          headers.forEach((header, colIndex) => {
            obj[header] = row[colIndex] || '';
          });
          return obj;
        }).filter(row => Object.values(row).some(val => val !== ''));

        const validationErrors = validateImportData(rows);
        if (validationErrors.length > 0) {
          setImportErrors(validationErrors);
          setImporting(false);
          return;
        }

        const usersToCreate = rows.map(row => {
          let statutValue = 1;
          if (row.statut_user) {
            const statut = row.statut_user.toString().toLowerCase();
            if (statut === 'actif' || statut === '1' || statut === 'true') statutValue = 1;
            else if (statut === 'inactif' || statut === '0' || statut === 'false') statutValue = 0;
          }

          let societeValue = null;
          if (currentProfile === "3" && currentSocieteId) {
            societeValue = currentSocieteId;
          }

          return {
            nom: row.nom.trim(),
            prenom: row.prenom.trim(),
            email: row.email.trim(),
            phone: row.phone ? row.phone.trim() : null,
            matricule: row.matricule ? row.matricule.trim() : null,
            profile: row.profile.toString().trim(),
            societe_id: societeValue,
            statut_user: statutValue,
            password: `Temp${Math.random().toString(36).substr(2, 8)}!`
          };
        });

        const token = localStorage.getItem("access_token");
        if (!token) {
          setImportErrors(["Session expirée. Veuillez vous reconnecter."]);
          setImporting(false);
          return;
        }

        let successCount = 0;
        let errorCount = 0;
        const errors = [];
        const results = [];

        for (const user of usersToCreate) {
          try {
            const endpoint = `${API_BASE_URL}/users/create.php`;
            
            const response = await axios.post(endpoint, user, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (response.data?.success) {
              successCount++;
              results.push(`✅ ${user.nom} ${user.prenom}: Créé avec succès`);
            } else {
              errorCount++;
              const errorMsg = `${user.nom} ${user.prenom}: ${response.data?.message || "Erreur inconnue"}`;
              errors.push(errorMsg);
              results.push(`❌ ${errorMsg}`);
            }
          } catch (err) {
            errorCount++;
            let errorMessage = `${user.nom} ${user.prenom}: `;
            if (err.response) {
              errorMessage += `Erreur ${err.response.status}: ${err.response.data?.message || "Erreur serveur"}`;
            } else if (err.request) {
              errorMessage += "Pas de réponse du serveur";
            } else {
              errorMessage += err.message;
            }
            errors.push(errorMessage);
            results.push(`❌ ${errorMessage}`);
          }
        }

        const summary = [];
        if (successCount > 0) summary.push(`✅ ${successCount} utilisateur(s) créé(s) avec succès`);
        if (errorCount > 0) summary.push(`❌ ${errorCount} erreur(s) lors de la création`);
        
        setImportResults([...summary, ...results]);
        setImportErrors(summary.concat(errors.slice(0, 5)));

        if (successCount > 0) {
          setTimeout(() => {
            fetchData();
            setInfoMsg(`${successCount} utilisateur(s) importé(s) avec succès`);
          }, 1500);
        }

        setImporting(false);
      };

      reader.readAsArrayBuffer(importFile);
    } catch (error) {
      setImportErrors(["Erreur lors de la lecture du fichier Excel", error.message]);
      setImporting(false);
    }
  };

  // ============================================
  // 🔥 PERMISSIONS
  // ============================================
  const shouldShowCreateButton = () => {
    if (!currentProfile) return false;
    const profileStr = String(currentProfile).trim();
    return profileStr === "1" || profileStr === "3";
  };

  // Le filtre par société n'est PAS revérifié ici volontairement : la liste
  // elle-même vient déjà scopée à la société du gestionnaire par
  // users/list.php (WHERE id_societe = ?), et chaque action mutante
  // (reset_password.php, update.php, delete.php, change_status.php)
  // revalide indépendamment la société côté backend. Comparer en plus à
  // currentSocieteId (dérivé du localStorage) ici était redondant et fragile
  // — si cette valeur était un jour désynchronisée (session ancienne, refresh
  // de token, etc.), TOUS les boutons d'action disparaissaient silencieusement
  // pour le gestionnaire alors que le backend aurait autorisé l'action.
  const canEditUser = (userProfileRaw) => {
    // users/list.php ne force pas le type de `profile` en string (contrairement
    // à `societe_id`, casté en int) — mysqli peut le renvoyer en tant que
    // nombre JSON selon le typage réel de la colonne en base. Sans ce
    // String(), une comparaison stricte contre "2"/"4"/"5" échouait toujours
    // silencieusement, faisant disparaître TOUS les boutons d'action pour le
    // gestionnaire (même bug que profileLabel()/profileIcon() plus haut,
    // déjà protégés par un String() explicite).
    const userProfile = String(userProfileRaw);
    if (currentProfile === "1") {
      return userProfile === "1" || userProfile === "3";
    }
    if (currentProfile === "3") {
      return userProfile === "2" || userProfile === "4" || userProfile === "5";
    }
    return false;
  };

  const canDeleteUser = (userProfileRaw, userStatut) => {
    if (userStatut === 0) return false;
    const userProfile = String(userProfileRaw);
    if (currentProfile === "1") {
      return userProfile === "1" || userProfile === "3";
    }
    if (currentProfile === "3") {
      return userProfile === "2" || userProfile === "4" || userProfile === "5";
    }
    return false;
  };

  // ============================================
  // 🔥 PAGINATION
  // ============================================
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.length > 0 ? filteredUsers.slice(indexOfFirstItem, indexOfLastItem) : [];
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / itemsPerPage));

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // ============================================
  // 🔥 FILTRES
  // ============================================
  const resetSearch = () => setSearchTerm("");
  const resetFilters = () => {
    setSearchTerm("");
    setFilterStatut("");
    setFilterProfile("");
  };
  const hasActiveFilters = () => searchTerm || filterStatut || filterProfile;

  // ============================================
  // 🔥 UTILITAIRES
  // ============================================
  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  // ============================================
  // 🔥 RENDU
  // ============================================
  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des utilisateurs</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Utilisateurs</li>
          </ol>
        </nav>
      </div>

      {currentProfile === "4" ? (
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#F5F7FA',
                    color: '#B0BEC5'
                  }}>
                    <i className="bi bi-lock fs-1"></i>
                  </div>
                </div>
                <h5 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Accès restreint</h5>
                <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                  Vous n'avez pas les permissions nécessaires pour accéder à cette page.
                </p>
                <button
                  className="btn fw-semibold mt-4"
                  onClick={() => navigate("/dashboard")}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px'
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour au tableau de bord
                </button>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              {/* HEADER AVEC BOUTONS */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 className="card-title mb-1" style={{ 
                    color: COLORS.primary,
                    borderBottom: `2px solid ${COLORS.lightBg}`,
                    paddingBottom: '16px'
                  }}>
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                        width: '36px',
                        height: '36px',
                        backgroundColor: '#E3F2FD',
                        color: COLORS.primary
                      }}>
                        <i className="bi bi-people"></i>
                      </div>
                      Liste des utilisateurs
                    </div>
                  </h5>
                  <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    {filteredUsers.length} utilisateur(s) trouvé(s)
                    {currentUserId && " (vous ne figurez pas dans cette liste)"}
                  </p>
                </div>
                
                <div className="d-flex gap-2">
                  {hasActiveFilters() && canUseFilters() && (
                    <button
                      className="btn fw-semibold"
                      onClick={resetFilters}
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 20px'
                      }}
                    >
                      <i className="bi bi-arrow-clockwise me-2"></i>
                      Réinitialiser
                    </button>
                  )}
                 
                  {shouldShowCreateButton() && (
                    <button
                      className="btn fw-semibold"
                      onClick={handleCreateUser}
                      style={{
                        color: COLORS.white,
                        backgroundColor: COLORS.primary,
                        borderColor: COLORS.primary,
                        borderRadius: '8px',
                        padding: '10px 24px'
                      }}
                    >
                      <i className="bi bi-plus-lg me-2"></i>
                      Nouvel utilisateur
                    </button>
                  )}

                  {currentProfile === "3" && (
  <Link
    to="/users/import"
    className="btn fw-semibold"
    style={{
      color: COLORS.white,
      backgroundColor: COLORS.success,
      borderColor: COLORS.success,
      borderRadius: '8px',
      padding: '10px 24px'
    }}
  >
    <i className="bi bi-file-earmark-excel me-2"></i>
    Importer Excel
  </Link>
)}
                </div>
              </div>

              {/* MESSAGES D'ERREUR/INFO */}
              {serverError && (
                <div className="alert alert-dismissible fade show mb-4" style={{ 
                  backgroundColor: '#ffebee',
                  borderColor: COLORS.danger,
                  color: '#c62828',
                  borderRadius: '8px',
                  borderLeft: `4px solid ${COLORS.danger}`
                }}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <div className="flex-grow-1">{serverError}</div>
                    <button type="button" className="btn-close" onClick={() => setServerError("")}></button>
                  </div>
                </div>
              )}
              
              {infoMsg && !loading && (
                <div className="alert alert-success alert-dismissible fade show mb-4" style={{
                  backgroundColor: '#e8f5e9',
                  borderColor: COLORS.success,
                  color: '#2e7d32',
                  borderRadius: '8px',
                  borderLeft: `4px solid ${COLORS.success}`
                }}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-info-circle me-2"></i>
                    <div className="flex-grow-1">{infoMsg}</div>
                    <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                  </div>
                </div>
              )}

              {/* FILTRES - UNIQUEMENT POUR ADMIN ET GESTIONNAIRE */}
              {canUseFilters() ? (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card" style={{ backgroundColor: '#E3F2FD', border: 'none', borderRadius: '8px' }}>
                      <div className="card-body py-3">
                        <div className="row g-3">
                          {/* RECHERCHE - POUR TOUS */}
                          <div className="col-md-4">
                            <div className="input-group">
                              <span className="input-group-text" style={{ backgroundColor: COLORS.lightBg, borderColor: '#e0e0e0' }}>
                                <i className="bi bi-search"></i>
                              </span>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Rechercher (nom, prénom, email, matricule)..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ borderColor: '#e0e0e0' }}
                              />
                              {searchTerm && (
                                <button
                                  className="btn"
                                  type="button"
                                  onClick={resetSearch}
                                  style={{
                                    color: COLORS.primary,
                                    borderColor: '#e0e0e0',
                                    backgroundColor: COLORS.white,
                                    borderLeft: 'none'
                                  }}
                                >
                                  <i className="bi bi-x"></i>
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {/* FILTRE STATUT - ADMIN ET GESTIONNAIRE */}
                          {canUseStatutFilter() && (
                            <div className="col-md-4">
                              <div className="input-group">
                                <span className="input-group-text" style={{ backgroundColor: COLORS.lightBg, borderColor: '#e0e0e0' }}>
                                  <i className="bi bi-funnel"></i>
                                </span>
                                <select 
                                  className="form-select"
                                  value={filterStatut}
                                  onChange={(e) => setFilterStatut(e.target.value)}
                                  style={{ borderColor: '#e0e0e0' }}
                                >
                                  <option value="">Tous les statuts</option>
                                  <option value="actif">Actif</option>
                                  <option value="inactif">Inactif</option>
                                </select>
                              </div>
                            </div>
                          )}

                          {/* FILTRE PROFIL - ADMIN ET GESTIONNAIRE AVEC OPTIONS FILTRÉES */}
                          {canUseProfileFilter() && (
                            <div className="col-md-4">
                              <div className="input-group">
                                <span className="input-group-text" style={{ backgroundColor: COLORS.lightBg, borderColor: '#e0e0e0' }}>
                                  <i className="bi bi-person-badge"></i>
                                </span>
                                <select 
                                  className="form-select"
                                  value={filterProfile}
                                  onChange={(e) => setFilterProfile(e.target.value)}
                                  style={{ borderColor: '#e0e0e0' }}
                                >
                                  <option value="">Tous les profils</option>
                                  {getFilteredProfileOptions().map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="mt-3 d-flex justify-content-between align-items-center">
                          <div className="text-muted small">
                            <i className="bi bi-info-circle me-1"></i>
                            <span className="badge fw-semibold me-2" style={{ 
                              backgroundColor: COLORS.lightBg,
                              color: COLORS.primary,
                              padding: '4px 12px',
                              borderRadius: '6px'
                            }}>
                              {filteredUsers.length} utilisateur(s) affiché(s)
                            </span>
                          </div>
                          
                          <button
                            onClick={fetchData}
                            className="btn fw-semibold"
                            disabled={loading}
                            style={{
                              color: COLORS.primary,
                              borderColor: COLORS.primary,
                              backgroundColor: 'transparent',
                              borderRadius: '8px',
                              padding: '6px 16px',
                              fontSize: '0.85rem'
                            }}
                          >
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            Actualiser
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* POUR LE CONTRÔLEUR - BARRE DE RECHERCHE UNIQUEMENT */
                currentProfile === "2" && (
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="card" style={{ backgroundColor: '#E3F2FD', border: 'none', borderRadius: '8px' }}>
                        <div className="card-body py-3">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="input-group">
                                <span className="input-group-text" style={{ backgroundColor: COLORS.lightBg, borderColor: '#e0e0e0' }}>
                                  <i className="bi bi-search"></i>
                                </span>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Rechercher un agent..."
                                  value={searchTerm}
                                  onChange={(e) => setSearchTerm(e.target.value)}
                                  style={{ borderColor: '#e0e0e0' }}
                                />
                                {searchTerm && (
                                  <button
                                    className="btn"
                                    type="button"
                                    onClick={resetSearch}
                                    style={{
                                      color: COLORS.primary,
                                      borderColor: '#e0e0e0',
                                      backgroundColor: COLORS.white,
                                      borderLeft: 'none'
                                    }}
                                  >
                                    <i className="bi bi-x"></i>
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="col-md-6 text-end">
                              <button
                                onClick={fetchData}
                                className="btn fw-semibold"
                                disabled={loading}
                                style={{
                                  color: COLORS.primary,
                                  borderColor: COLORS.primary,
                                  backgroundColor: 'transparent',
                                  borderRadius: '8px',
                                  padding: '6px 16px',
                                  fontSize: '0.85rem'
                                }}
                              >
                                <i className="bi bi-arrow-clockwise me-1"></i>
                                Actualiser
                              </button>
                            </div>
                          </div>
                          <div className="mt-2 text-muted small">
                            <i className="bi bi-info-circle me-1"></i>
                            Vous pouvez rechercher des agents de votre société.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}

              {/* LISTE DES UTILISATEURS */}
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                  <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des utilisateurs...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-5">
                  <div className="mb-4">
                    <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                      width: '80px',
                      height: '80px',
                      backgroundColor: '#F5F7FA',
                      color: '#B0BEC5'
                    }}>
                      <i className="bi bi-people fs-1"></i>
                    </div>
                  </div>
                  <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun autre utilisateur trouvé</h6>
                  <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                    {currentProfile === "1" 
                      ? "Vous êtes le seul administrateur. Aucun autre administrateur ou gestionnaire n'a été créé."
                      : currentProfile === "3"
                      ? "Aucun contrôleur ou agent n'a été créé dans votre société."
                      : "Aucun agent n'a été trouvé dans votre société."}
                  </p>
                  {shouldShowCreateButton() && (
                    <button
                      onClick={handleCreateUser}
                      className="btn fw-semibold mt-3"
                      style={{
                        color: COLORS.white,
                        backgroundColor: COLORS.primary,
                        borderColor: COLORS.primary,
                        borderRadius: '8px',
                        padding: '10px 24px'
                      }}
                    >
                      <i className="bi bi-plus-lg me-2"></i>
                      Créer un utilisateur
                    </button>
                  )}
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-5">
                  <div className="mb-4">
                    <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                      width: '80px',
                      height: '80px',
                      backgroundColor: '#F5F7FA',
                      color: '#B0BEC5'
                    }}>
                      <i className="bi bi-search fs-1"></i>
                    </div>
                  </div>
                  <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun résultat</h6>
                  <p className="text-muted" style={{ color: COLORS.darkText }}>
                    Aucun utilisateur ne correspond aux critères de recherche.
                  </p>
                  <button
                    className="btn fw-semibold mt-3"
                    onClick={resetFilters}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 24px'
                    }}
                  >
                    <i className="bi bi-x-lg me-2"></i>
                    Effacer les filtres
                  </button>
                </div>
              ) : (
                <>
                  {/* TABLEAU */}
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr style={{ backgroundColor: COLORS.lightBg, borderBottom: `2px solid ${COLORS.accent}` }}>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Utilisateur</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Contact</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Profil</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Matricule</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Statut</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Créé le</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map((user, index) => {
                          const numStatus = Number(user.statut_user);
                          const status = statusLabel(numStatus);
                          const canEdit = canEditUser(user.profile);
                          const canDelete = canDeleteUser(user.profile, numStatus);
                          const isUpdating = updatingId === user.id;

                          return (
                            <tr key={user.id || `row-${index}`} style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
                              <td style={{ padding: '16px' }}>
                                <div className="d-flex align-items-center">
                                  <div className="me-3">
                                    <div 
                                      className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                                      style={{ 
                                        width: '44px', 
                                        height: '44px', 
                                        fontSize: '15px',
                                        backgroundColor: COLORS.primary
                                      }}
                                    >
                                      {user.prenom?.charAt(0) || ''}{user.nom?.charAt(0) || ''}
                                    </div>
                                  </div>
                                  <div>
                                    <div style={{ color: COLORS.primary, fontWeight: '500', fontSize: '0.95rem' }}>
                                      {user.prenom} {user.nom}
                                    </div>
                                    <div className="small" style={{ color: '#6c757d', fontSize: '0.8rem' }}>
                                      {user.email}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <div>
                                  <div className="d-flex align-items-center mb-1">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                      width: '20px',
                                      height: '20px',
                                      backgroundColor: '#E3F2FD',
                                      color: COLORS.secondary,
                                      fontSize: '0.7rem'
                                    }}>
                                      <i className="bi bi-envelope"></i>
                                    </div>
                                    <small style={{ color: COLORS.darkText }}>{user.email}</small>
                                  </div>
                                  <div className="d-flex align-items-center">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                      width: '20px',
                                      height: '20px',
                                      backgroundColor: '#E3F2FD',
                                      color: COLORS.secondary,
                                      fontSize: '0.7rem'
                                    }}>
                                      <i className="bi bi-telephone"></i>
                                    </div>
                                    <small style={{ color: COLORS.darkText }}>{user.phone || '-'}</small>
                                  </div>
                                </div>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <div className="d-flex align-items-center">
                                  <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                    width: '24px',
                                    height: '24px',
                                    backgroundColor: '#E3F2FD',
                                    color: COLORS.primary
                                  }}>
                                    <i className={`bi ${profileIcon(user.profile)}`}></i>
                                  </div>
                                  <span className="badge fw-semibold" style={{ 
                                    backgroundColor: '#E3F2FD',
                                    color: COLORS.primary,
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '0.8rem'
                                  }}>
                                    {profileLabel(user.profile)}
                                  </span>
                                </div>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                {user.matricule ? (
                                  <span className="badge fw-semibold" style={{ 
                                    backgroundColor: COLORS.lightBg,
                                    color: COLORS.darkText,
                                    border: `1px solid #e0e0e0`,
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    fontSize: '0.8rem'
                                  }}>
                                    <i className="bi bi-card-text me-1"></i>
                                    {user.matricule}
                                  </span>
                                ) : '-'}
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <span className="badge fw-semibold" style={{ 
                                  backgroundColor: numStatus === 1 ? '#e8f5e9' : '#f5f5f5',
                                  color: numStatus === 1 ? '#2e7d32' : '#757575',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontSize: '0.8rem'
                                }}>
                                  <i className={`bi ${status.icon} me-1`}></i>
                                  {status.text}
                                </span>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                                  {formatDateTime(user.created_at).split(' ')[0]}
                                </small>
                                <br />
                                <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                  {formatDateTime(user.created_at).split(' ')[1]}
                                </small>
                              </td>
                              
                              <td style={{ padding: '16px', textAlign: 'center' }}>
                                <div className="d-flex gap-2 justify-content-center">
                                  <button
                                    className="btn btn-outline-info fw-semibold"
                                    onClick={() => handleViewDetails(user)}
                                    disabled={isUpdating}
                                    style={{
                                      color: COLORS.primary,
                                      borderRadius: '6px',
                                      padding: '8px 16px',
                                      fontSize: '0.85rem',
                                      minWidth: '44px'
                                    }}
                                  >
                                    <i className="bi bi-eye"></i>
                                  </button>
                                  
                                  {canEdit && numStatus === 1 && (
                                    <button
                                      className="btn fw-semibold"
                                      onClick={() => openResetPasswordModal(user)}
                                      disabled={isUpdating}
                                      style={{
                                        color: '#4caf50',
                                        borderColor: '#4caf50',
                                        backgroundColor: 'transparent',
                                        borderRadius: '6px',
                                        padding: '8px 16px',
                                        fontSize: '0.85rem',
                                        minWidth: '44px'
                                      }}
                                    >
                                      <i className="bi bi-key"></i>
                                    </button>
                                  )}

                                  {canEdit && (
                                    <button
                                      className="btn fw-semibold"
                                      onClick={() => openStatusModal(user)}
                                      disabled={isUpdating}
                                      style={{
                                        color: numStatus === 1 ? '#ff9800' : '#4caf50',
                                        borderColor: numStatus === 1 ? '#ff9800' : '#4caf50',
                                        backgroundColor: 'transparent',
                                        borderRadius: '6px',
                                        padding: '8px 16px',
                                        fontSize: '0.85rem',
                                        minWidth: '44px'
                                      }}
                                    >
                                      {isUpdating ? (
                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                      ) : (
                                        <i className={`bi ${numStatus === 1 ? 'bi-person-x' : 'bi-person-check'}`}></i>
                                      )}
                                    </button>
                                  )}

                                  {canDelete && numStatus === 1 && (
                                    <button
                                      className="btn fw-semibold"
                                      onClick={() => openDeleteModal(user)}
                                      disabled={isUpdating}
                                      style={{
                                        color: COLORS.danger,
                                        borderColor: COLORS.danger,
                                        backgroundColor: 'transparent',
                                        borderRadius: '6px',
                                        padding: '8px 16px',
                                        fontSize: '0.85rem',
                                        minWidth: '44px'
                                      }}
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* PAGINATION */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div className="d-flex align-items-center">
                        <span className="me-2" style={{ color: COLORS.darkText }}>Afficher :</span>
                        <select 
                          className="form-select form-select-sm w-auto"
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                          style={{ borderColor: '#e0e0e0', borderRadius: '6px' }}
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                        </select>
                        <span className="ms-3 text-muted small">
                          {filteredUsers.length} utilisateur(s) au total
                        </span>
                      </div>
                      
                      <nav>
                        <ul className="pagination pagination-sm mb-0">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button 
                              className="page-link"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              style={{ color: COLORS.primary }}
                            >
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>
                          
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            
                            return (
                              <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                <button 
                                  className="page-link"
                                  onClick={() => handlePageChange(pageNum)}
                                  style={{
                                    backgroundColor: currentPage === pageNum ? COLORS.primary : 'transparent',
                                    borderColor: currentPage === pageNum ? COLORS.primary : '#dee2e6',
                                    color: currentPage === pageNum ? 'white' : COLORS.primary
                                  }}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            );
                          })}
                          
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button 
                              className="page-link"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              style={{ color: COLORS.primary }}
                            >
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* MODAL CONFIRMATION STATUS */}
      {selectedUser && modalAction && modalAction !== 'delete' && modalAction !== 'resetPassword' && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: modalAction === 'desactiver' ? '#fff3e0' : '#e8f5e9' }}>
                <h5 className="modal-title" style={{ color: modalAction === 'desactiver' ? COLORS.warning : COLORS.success }}>
                  <i className={`bi ${modalAction === 'desactiver' ? 'bi-person-x' : 'bi-person-check'} me-2`}></i>
                  {modalAction === 'desactiver' ? 'Désactiver' : 'Activer'} l'utilisateur
                </h5>
                <button type="button" className="btn-close" onClick={closeAllModals}></button>
              </div>
              <div className="modal-body">
                <p>{modalMessage}</p>
                <p className="text-muted small">
                  {modalAction === 'desactiver' 
                    ? "L'utilisateur ne pourra plus se connecter à la plateforme."
                    : "L'utilisateur pourra à nouveau se connecter à la plateforme."}
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeAllModals}>Annuler</button>
                <button 
                  className={`btn ${modalAction === 'desactiver' ? 'btn-warning' : 'btn-success'}`}
                  onClick={handleChangeStatus}
                  disabled={updatingId === selectedUser.id}
                >
                  {updatingId === selectedUser.id ? 'Traitement...' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL RÉINITIALISATION MOT DE PASSE */}
      {selectedUser && modalAction === 'resetPassword' && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#fff3e0' }}>
                <h5 className="modal-title" style={{ color: COLORS.warning }}>
                  <i className="bi bi-key me-2"></i>
                  Réinitialiser le mot de passe
                </h5>
                <button type="button" className="btn-close" onClick={closeAllModals}></button>
              </div>
              <div className="modal-body">
                <p>{modalMessage}</p>
                <p className="text-muted small">
                  Un nouveau mot de passe temporaire sera généré et envoyé à l'utilisateur.
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeAllModals}>Annuler</button>
                <button 
                  className="btn btn-warning"
                  onClick={handleResetPassword}
                  disabled={updatingId === selectedUser.id}
                >
                  {updatingId === selectedUser.id ? 'Traitement...' : 'Réinitialiser'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SUPPRESSION */}
      {selectedUser && modalAction === 'delete' && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#ffebee' }}>
                <h5 className="modal-title" style={{ color: COLORS.danger }}>
                  <i className="bi bi-trash me-2"></i>
                  Supprimer l'utilisateur
                </h5>
                <button type="button" className="btn-close" onClick={closeAllModals}></button>
              </div>
              <div className="modal-body">
                <p>{modalMessage}</p>
                <p className="text-danger small">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  Cette action est irréversible. L'utilisateur sera définitivement supprimé.
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeAllModals}>Annuler</button>
                <button 
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={updatingId === selectedUser.id}
                >
                  {updatingId === selectedUser.id ? 'Traitement...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL IMPORT EXCEL */}
      {showImportModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#E3F2FD' }}>
                <h5 className="modal-title" style={{ color: COLORS.primary }}>
                  <i className="bi bi-file-earmark-excel me-2"></i>
                  Importer des utilisateurs
                </h5>
                <button type="button" className="btn-close" onClick={closeAllModals} disabled={importing}></button>
              </div>
              <div className="modal-body">
                {/* Instructions et formulaire d'import */}
                <div className="mb-4">
                  <div className="alert alert-info" style={{ backgroundColor: '#E3F2FD', borderColor: COLORS.info }}>
                    <i className="bi bi-info-circle me-2"></i>
                    Téléchargez le modèle Excel, remplissez-le avec vos données et importez-le.
                    <button className="btn btn-sm btn-outline-primary ms-3" onClick={downloadExcelTemplate}>
                      <i className="bi bi-download me-1"></i>
                      Modèle
                    </button>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Fichier Excel</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      disabled={importing}
                    />
                    <small className="text-muted">Formats acceptés: .xlsx, .xls</small>
                  </div>

                  {importPreview.length > 0 && (
                    <div className="mt-3">
                      <h6 className="fw-semibold">Aperçu ({importPreview.length} lignes)</h6>
                      <div className="table-responsive" style={{ maxHeight: '200px' }}>
                        <table className="table table-sm table-bordered">
                          <thead>
                            <tr>
                              {Object.keys(importPreview[0]).map(key => (
                                <th key={key} style={{ fontSize: '0.8rem' }}>{key}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {importPreview.slice(0, 5).map((row, i) => (
                              <tr key={i}>
                                {Object.values(row).map((val, j) => (
                                  <td key={j} style={{ fontSize: '0.8rem' }}>{val || '-'}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {importErrors.length > 0 && (
                    <div className="alert alert-danger mt-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <h6 className="fw-semibold">Erreurs :</h6>
                      <ul className="mb-0 small">
                        {importErrors.slice(0, 10).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                        {importErrors.length > 10 && <li>... et {importErrors.length - 10} autre(s)</li>}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeAllModals} disabled={importing}>
                  Annuler
                </button>
                <button
                  className="btn btn-success"
                  onClick={processImport}
                  disabled={!importFile || importing}
                >
                  {importing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-upload me-2"></i>
                      Importer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modal {
          overflow-y: auto;
        }
        .page-link:focus {
          box-shadow: none;
        }
        .table-hover tbody tr:hover {
          background-color: rgba(30, 136, 200, 0.05);
          transition: background-color 0.2s;
        }
        @media (max-width: 768px) {
          .d-flex.gap-2 {
            flex-wrap: wrap;
          }
          .btn {
            width: 100%;
          }
        }
      `}</style>

      {/* Mot de passe réinitialisé — modale bloquante avec masquage, plutôt
          qu'un bandeau en haut de page qui restait exposé hors écran une fois
          la liste parcourue vers le bas. */}
      {resetPasswordResult && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '12px' }}>
              <div className="modal-header" style={{
                backgroundColor: '#fff3e0',
                borderBottom: `1px solid ${COLORS.warning}`,
                borderRadius: '12px 12px 0 0'
              }}>
                <h5 className="modal-title" style={{ color: COLORS.darkText, fontWeight: '600' }}>
                  <i className="bi bi-key me-2"></i>
                  Mot de passe réinitialisé
                </h5>
              </div>
              <div className="modal-body">
                <p style={{ color: COLORS.darkText }}>
                  {resetPasswordResult.warning || "Communiquez ce mot de passe à l'utilisateur :"}
                </p>
                <div className="d-flex align-items-center gap-2 p-2" style={{
                  backgroundColor: COLORS.lightBg,
                  borderRadius: '8px',
                  border: '1px dashed ' + COLORS.warning
                }}>
                  <code className="flex-grow-1" style={{ fontSize: '1.1rem', fontWeight: '700', letterSpacing: '0.05em' }}>
                    {passwordMasque ? '•'.repeat(Math.max(resetPasswordResult.password.length, 8)) : resetPasswordResult.password}
                  </code>
                  <button
                    type="button"
                    className="btn btn-sm fw-semibold"
                    onClick={() => setPasswordMasque(!passwordMasque)}
                    style={{ backgroundColor: 'transparent', border: '1px solid #6c757d', color: '#6c757d', borderRadius: '6px' }}
                    title={passwordMasque ? 'Afficher' : 'Masquer'}
                  >
                    <i className={`bi ${passwordMasque ? 'bi-eye' : 'bi-eye-slash'}`}></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm fw-semibold"
                    onClick={() => {
                      navigator.clipboard?.writeText(resetPasswordResult.password);
                      setPasswordCopie(true);
                      setTimeout(() => setPasswordCopie(false), 2000);
                    }}
                    style={{ color: COLORS.primary, borderColor: COLORS.primary, borderRadius: '6px' }}
                  >
                    <i className={`bi ${passwordCopie ? 'bi-check-lg' : 'bi-clipboard'} me-1`}></i>
                    {passwordCopie ? 'Copié' : 'Copier'}
                  </button>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: `1px solid ${COLORS.lightBg}` }}>
                <button
                  type="button"
                  className="btn fw-semibold"
                  style={{ backgroundColor: COLORS.primary, color: 'white', borderRadius: '8px' }}
                  onClick={() => { setResetPasswordResult(null); setPasswordCopie(false); setPasswordMasque(true); }}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default UserList;